import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';

import type { ControlType, PanelInventory, Vendor } from '@pedestal-panels/contracts';
import { parsePanelInventory } from '@pedestal-panels/contracts';
import {
  aspectRatioFromMm,
  positionStyle,
  tagForControlType,
} from '@pedestal-panels/core';

import '@pedestal-panels/core';

import inventoryJson from '../inventory.json' with { type: 'json' };

/**
 * Grass Valley OCP-400 — Operational Control Panel.
 *
 * Active-shading portrait panel for the LDX-family camera ecosystem.
 * Anatomy verified against the v14.0 User's Guide (3922 496 49931,
 * December 2016); chassis dimensions 354 x 82 x 85 mm from section
 * 5.2 Dimensions; illumination colour scheme (saturated green LCD,
 * green soft-button LEDs, amber 7-segment iris display) confirmed by
 * the in-production photograph held under research/sources/.
 *
 * The composition is inventory-driven: it iterates the verified
 * `inventory.json`, dispatches each control through the
 * control-type-to-tag registry, and positions every primitive
 * absolutely via percentages derived from the canonical viewBox.
 * No px units appear in this file.
 */
export const meta = {
  id: 'ocp-400',
  vendor: 'grassvalley' as Vendor,
  model: 'OCP-400',
  productName: 'Grass Valley OCP-400 Operational Control Panel',
} as const;

// Inventory validated against the contract schema at module load so any
// drift between inventory.json and PanelInventorySchema fails fast in the
// browser console, not silently at render time. The validator CLI gives
// us the build-time gate; this gives us the runtime guard.
const inventory: PanelInventory = parsePanelInventory(inventoryJson);

@customElement('panel-ocp-400')
export class PanelOcp400Element extends LitElement {
  static override styles = [
    css`
      :host {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        container-type: inline-size;

        /* Bridge vendor token surface to primitive variables. */
        --control-cap: var(--control-rotary-cap);
        --control-cap-highlight: color-mix(in oklab, var(--control-rotary-cap) 55%, white);
        --control-skirt: var(--control-rotary-shaft);
        --control-pointer: var(--control-button-engrave);
        --control-shaft: color-mix(in oklab, var(--control-rotary-shaft) 80%, black);
        --control-collar-primary: var(--control-rotary-shaft);
        --control-detent-line: var(--chassis-accent-2, #000);
        --chassis-recess: color-mix(in oklab, var(--chassis-colour) 60%, black);
        --tally-off: var(--illumination-off);
        --encoder-led-colour: var(--illumination-function-on, #c89a14);
      }

      .stage {
        display: grid;
        place-items: center;
        block-size: 100%;
        inline-size: 100%;
        padding: 1.2cqi;
        box-sizing: border-box;
      }

      .panel {
        /* Portrait orientation; chassis height drives container scale.
         * block-size first, aspect-ratio computes inline-size, max keeps us
         * inside the stage even on extremely short viewports. */
        block-size: 100%;
        aspect-ratio: var(--ocp-aspect, 82 / 354);
        max-inline-size: 100%;
        position: relative;
      }

      chassis-frame {
        block-size: 100%;
        inline-size: 100%;
        position: relative;
        /* Override the default chassis aspect from packages/core so this
         * panel's portrait ratio takes effect on the SVG-less housing. */
        --chassis-aspect: var(--ocp-aspect, 82 / 354);
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    this.dataset['vendor'] = inventory.panel.vendor;
    this.style.setProperty('--ocp-aspect', aspectRatioFromMm(
      inventory.panel.dimensionsMm.width,
      inventory.panel.dimensionsMm.height,
    ));
  }

  override render() {
    return html`
      <div class="stage">
        <div class="panel">
          <chassis-frame
            view-box=${`0 0 ${inventory.panel.canonicalViewBox.width} ${inventory.panel.canonicalViewBox.height}`}
          >
            ${inventory.controls.map((control) => this.#renderControl(control))}
          </chassis-frame>
        </div>
      </div>
    `;
  }

  #renderControl(control: PanelInventory['controls'][number]) {
    const tag = unsafeStatic(tagForControlType(control.type as ControlType));
    const derived = this.#derivedGeometryFor(control);
    const position = derived?.position ?? control.position;
    const dimensions = derived?.dimensions ?? control.dimensions;
    const positioning = positionStyle(
      position,
      dimensions,
      inventory.panel.canonicalViewBox,
    );
    const style = `${positioning};${this.#colourOverridesFor(control)}`;
    const label = control.label || nothing;
    const tallyState = this.#tallyStateFor(control);
    const tallyShape = this.#tallyShapeFor(control);
    const isStackedSegment = control.type === 'display-segment';
    const displayKind = this.#displayKindFor(control);
    const placeholder = this.#displayPlaceholderFor(control);
    const featureShape = this.#chassisFeatureShapeFor(control);
    const buttonState = this.#buttonStateFor(control);

    if (control.type === 'chassis-feature') {
      const variant = this.#chassisFeatureVariantFor(control, featureShape);
      const brand = featureShape === 'logo' ? 'grass-valley' : '';
      return staticHtml`<chassis-feature-shape
        style=${style}
        shape=${featureShape}
        variant=${variant}
        brand=${brand}
        legend-align="middle"
        label=${control.label ?? ''}
      ></chassis-feature-shape>`;
    }

    const maxChars = this.#maxCharsFor(control);
    const isButton = control.type.startsWith('push-');
    // The OCP-400 rotaries have no lit ring halo at the base — the
    // channel cue is carried entirely by the coloured stripe across
    // the rubber cap (see <rotary-encoder>). The `illumination-ring`
    // bit on the inventory describes the physical hardware capability,
    // not the on-screen rendering.
    const ledRing = false;
    const illuminatedRotary = false;

    return staticHtml`<${tag}
      style=${style}
      label=${label}
      state=${isButton ? buttonState : tallyState ?? ''}
      ?led-ring=${ledRing}
      ?illuminated=${illuminatedRotary}
      shape=${tallyShape ?? ''}
      kind=${displayKind ?? ''}
      placeholder=${placeholder ?? ''}
      max-chars=${maxChars ?? 0}
      mode=${isStackedSegment || displayKind ? 'placeholder' : ''}
    ></${tag}>`;
  }

  /**
   * Compute the button state from the inventory's illumination block.
   *
   *   - `defaultState: "on"`              → 'default' (soft glow)
   *   - `defaultState: "state-dependent"` → 'off' until something tells
   *                                          the panel otherwise; arrows
   *                                          under the menu display
   *                                          live here
   *   - `defaultState: "off"`             → 'off'
   *
   * The 'selected' and 'error' states are reserved for future
   * interaction; the static render shows the powered-on default state
   * the operator sees on a working panel.
   */
  #buttonStateFor(control: PanelInventory['controls'][number]): string {
    const defaultState = control.illumination?.defaultState;
    if (defaultState === 'on') return 'default';
    return 'off';
  }

  /**
   * Compose the per-control CSS variable overrides that drive each
   * primitive's lit colour from the inventory's illumination block.
   *
   *   - Rotaries   → `--re-pointer` for the coloured diameter stripe
   *                  across the rubber cap (red, green, blue on the RGB
   *                  encoder rows; channel-indicator colours).
   *   - LEDs/tally → `--pb-led` for the stripe colour on the four-LED
   *                  tally bars (yellow outer, red inner per OCP-400 spec).
   *   - Buttons    → `--pb-led` for the soft-button illumination (dim
   *                  green normal, bright green selected — OCP-400 has
   *                  no blue button illumination).
   *   - Displays   → no override; dot-matrix and LCD palettes are driven
   *                  by `kind=` on the primitive, not by the inventory's
   *                  illumination colour (which represents the LED-class
   *                  emissive colour, not a CSS variable input).
   *
   * The illumination intent is recorded in
   * `inventory.json` `illumination.colours[0].hex` for each control.
   */
  #colourOverridesFor(control: PanelInventory['controls'][number]): string {
    const litColour = control.illumination?.colours?.[0]?.hex;
    if (!litColour) return '';
    if (control.type.startsWith('rotary-')) return `--re-pointer:${litColour}`;
    if (control.type.startsWith('display-')) return '';
    if (control.type.startsWith('push-') || control.type.startsWith('led-')) {
      return `--pb-led:${litColour}`;
    }
    return '';
  }

  #chassisFeatureShapeFor(
    control: PanelInventory['controls'][number],
  ): 'screw' | 'slot' | 'section' | 'legend' | 'logo' {
    if (control.id.includes('screw')) return 'screw';
    if (control.id.includes('slot')) return 'slot';
    if (
      control.id.includes('section') ||
      control.id.includes('plate') ||
      control.id.includes('lip')
    )
      return 'section';
    // Vendor wordmark — the Grass Valley silkscreen at the top-left
    // of the panel renders the GV corporate wordmark from the asset
    // library rather than typeset letterforms.
    if (control.id === 'gv-logo') return 'logo';
    return 'legend';
  }

  /**
   * Pick the variant attribute for a chassis-feature element. Amber for
   * engraved-text legends, section-deep for the LCD menu-recess (which
   * SVG draws with #010202 fill — visibly darker than the lower-chassis
   * section's outline-only stroke), section-recess for the joystick
   * lip (rounded-corner recess that frames the monoknob assembly),
   * section-plate for the raised button-row mounting plates, engraved
   * for everything else.
   */
  #chassisFeatureVariantFor(
    control: PanelInventory['controls'][number],
    shape: 'screw' | 'slot' | 'section' | 'legend' | 'logo',
  ): 'engraved' | 'amber' | 'section-deep' | 'section-recess' | 'section-plate' {
    if (shape === 'legend') return 'amber';
    if (shape === 'section') {
      if (control.id.includes('lcd')) return 'section-deep';
      if (control.id.includes('lip')) return 'section-recess';
      if (control.id.includes('plate')) return 'section-plate';
    }
    return 'engraved';
  }

  #tallyStateFor(control: PanelInventory['controls'][number]): string | null {
    if (control.type !== 'led-rgb' && control.type !== 'led-discrete') return null;
    return 'off';
  }

  #tallyShapeFor(control: PanelInventory['controls'][number]): string | null {
    if (control.type !== 'led-rgb' && control.type !== 'led-discrete') return null;
    return 'stripe';
  }

  /**
   * The OCP-400 carries two dot-matrix LED displays — the eight-character
   * camera identifier and the four-character iris F-number readout. Both
   * use the same yellow-green CRT-style emissive LEDs (the production
   * photograph shows them in the same colour). Amber is reserved for the
   * static engraved GAIN/BLACK labels under the RGB encoder rows, NOT
   * for any display.
   */
  #displayKindFor(
    control: PanelInventory['controls'][number],
  ): 'matrix-green' | 'lcd-green' | null {
    if (!control.type.startsWith('display-')) return null;
    if (control.id === 'menu-lcd') return 'lcd-green';
    return 'matrix-green';
  }

  #displayPlaceholderFor(control: PanelInventory['controls'][number]): string | null {
    if (control.id === 'camera-id-display') return 'camera';
    if (control.id === 'display-iris-fnum') return 'iris';
    if (control.id === 'menu-lcd') return 'menu';
    return null;
  }

  #maxCharsFor(control: PanelInventory['controls'][number]): number | null {
    if (control.id === 'camera-id-display') return 8;
    if (control.id === 'display-iris-fnum') return 4;
    return null;
  }

  /**
   * Resolve the geometry for derived elements at render time so the
   * canonical layout relationships from the OCP-400 doctrine drive
   * the positions, not hand-set inventory pixels.
   *
   *   - camera-id-display: spans from midpoint(A.cx, B.cx) to
   *     midpoint(C.cx, D.cx) across the top button row (A..D =
   *     PANEL LOCK / WHITE BALANCE / BARS / CALL). Height is taken
   *     from the inventory entry, which sets the shared dot-matrix
   *     module height the iris readout inherits.
   *   - tally bars (left/right pairs): each pair fits inside ONE
   *     soft-button column width (column A for the left pair, column
   *     D for the right). Outer/inner widths preserve the layout-SVG
   *     ratio (47 : 9 gap : 101). Height matches the dot-matrix
   *     display height (derived from camera-id-display).
   *   - display-iris-fnum: shares the dot-matrix module so its cell
   *     pitch equals camera-id-display.w / 8. With four characters
   *     the box width is cellPitch × 4. Height matches the cam-id
   *     module. Vertical centre aligned with FREE button's vertical
   *     centre (the iris readout pairs visually with FREE on the
   *     lower chassis row).
   *
   * Returns null for every other control — the caller falls back to
   * the inventory's own position/dimensions.
   */
  #derivedGeometryFor(
    control: PanelInventory['controls'][number],
  ): { position: { x: number; y: number }; dimensions: { w: number; h: number } } | null {
    const lookup = (id: string) => inventory.controls.find((c) => c.id === id);

    const camDisplay = () => {
      const colA = lookup('btn-panel-lock');
      const colB = lookup('btn-white-balance');
      const colC = lookup('btn-bars');
      const colD = lookup('btn-call');
      const camRaw = lookup('camera-id-display');
      const cardSlot = lookup('card-slot');
      if (!colA || !colB || !colC || !colD || !camRaw || !cardSlot) return null;
      const cx = (c: typeof colA) => c.position.x + c.dimensions.w / 2;
      const left = (cx(colA) + cx(colB)) / 2;
      const right = (cx(colC) + cx(colD)) / 2;
      // Vertical position derived: cam-id row is centred in the
      // chassis band between the card-slot's bottom edge above and
      // the top button row's top edge below. Equal margin above and
      // below the row — the only deterministic placement that
      // tracks both anchors when either moves.
      const bandTop = cardSlot.position.y + cardSlot.dimensions.h;
      const bandBottom = colA.position.y;
      const h = camRaw.dimensions.h;
      const y = bandTop + (bandBottom - bandTop - h) / 2;
      return {
        position: { x: left, y },
        dimensions: { w: right - left, h },
      };
    };

    if (control.id === 'camera-id-display') {
      return camDisplay();
    }

    if (control.id === 'display-iris-fnum') {
      const cam = camDisplay();
      const free = lookup('btn-free');
      if (!cam || !free) return null;
      const cellPitch = cam.dimensions.w / 8;
      const w = cellPitch * 4;
      const h = cam.dimensions.h;
      // Keep the inventory entry's horizontal anchor (its centre) so
      // the iris readout stays where the chassis silkscreen places
      // it; only the width is derived. Vertical centre snaps to FREE.
      const irisRaw = control;
      const origCx = irisRaw.position.x + irisRaw.dimensions.w / 2;
      const x = origCx - w / 2;
      const freeCy = free.position.y + free.dimensions.h / 2;
      const y = freeCy - h / 2;
      return { position: { x, y }, dimensions: { w, h } };
    }

    if (control.id.startsWith('tally-')) {
      const cam = camDisplay();
      if (!cam) return null;
      const isLeft = control.id.startsWith('tally-left');
      const col = lookup(isLeft ? 'btn-panel-lock' : 'btn-call');
      if (!col) return null;
      // Per the production photograph (research/sources/photos/...):
      // the yellow ISO bar and the red on-air bar in each pair are
      // RENDERED AT THE SAME WIDTH. Two equal bars + one gap fit
      // exactly inside the column's width.
      //
      // PROVISIONAL: GAP_FRACTION = 0.12 (~12 % of column width
      // between the two bars). Not measured from the layout SVG or
      // the photograph — picked qualitatively so the two LEDs read
      // as discrete lights rather than touching neighbours.
      const GAP_FRACTION = 0.12;
      const gapW = col.dimensions.w * GAP_FRACTION;
      const barW = (col.dimensions.w - gapW) / 2;
      const colLeft = col.position.x;
      // bar-1 / bar-2 semantics on the inventory side stays the same
      // (yellow ISO on the outer flank, red on-air on the inner
      // flank) — only the WIDTHS are equalised, not the colour
      // assignment.
      //   tally-left-bar-1  = yellow (outer / leftmost on left side)
      //   tally-left-bar-2  = red    (inner)
      //   tally-right-bar-1 = red    (inner / leftmost on right side)
      //   tally-right-bar-2 = yellow (outer)
      let barX = colLeft;
      if (control.id === 'tally-left-bar-1' || control.id === 'tally-right-bar-1') {
        barX = colLeft;
      } else {
        barX = colLeft + barW + gapW;
      }
      return {
        position: { x: barX, y: cam.position.y },
        dimensions: { w: barW, h: cam.dimensions.h },
      };
    }

    return null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'panel-ocp-400': PanelOcp400Element;
  }
}
